import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Users as UsersIcon, Download, X, Mail,
  Building2, UserPlus, Truck, UserCheck, CheckCircle,
  AlertCircle, RefreshCw, Trash2, Shield,
} from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import type { UserProfile } from '../../lib/api';
import { authAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription } from '../../components/ui/alert';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { useAuth } from '../../contexts/AuthContext';
import { useView } from '../../contexts/ViewContext';
import { UserDetailsModal } from './UserDetailsModal';
import {
  ChangeRoleDialog,
  ChangeCompanyDialog,
  ActivityLogDialog,
  BulkApproveDialog,
  BulkSendInvitationsDialog,
} from './UserManagementDialogs';
import UserTable from './UserTable';
import { useUserActions } from './useUserActions';
import type { SortField, SortDirection } from './UserTable';
import { exportUsersToCSV } from './usersExport';

/**
 * Mirrors the Status column's badges so the filter can select anything the
 * column can show. It previously offered only active/inactive, which left
 * "Invite Sent" — the one state an admin actually chases — unfilterable.
 */
type StatusFilter = 'all' | 'active' | 'inactive' | 'invited';

/** True when the row renders the amber "Invite Sent" badge. Kept beside the
 *  filter so the two cannot drift from UserTable's badge logic. */
const isAwaitingInvite = (u: UserProfile): boolean =>
  Boolean(u.user.is_active && u.user.invitation_sent && !u.user.has_joined && !u.user.last_login);
type ApprovalFilter = 'all' | 'approved' | 'pending';

export default function UsersManagement() {
  const navigate = useNavigate();
  const { user, startImpersonation } = useAuth();
  const { viewLevel, selectedGroupId, selectedCompanyId, selectedSiteId } = useView();
  const actions = useUserActions();

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  // CR-5312: default to Active so admins triaging the list don't see
  // deactivated users by default. The "All" / "Inactive" options stay
  // available for cleanup workflows.
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [approvalFilter, setApprovalFilter] = useState<ApprovalFilter>('all');
  const [companyFilter, setCompanyFilter] = useState<string>('all');
  const [roleFilter, setRoleFilter] = useState<string>('all');

  // Sorting & Pagination
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // Selection & Tabs
  const [selectedUsers, setSelectedUsers] = useState<Set<number>>(new Set());
  // CR-5367: third tab for Valuechain (internal Helix) users so the
  // Customer Users tab no longer doubles as a catch-all for everything
  // non-supplier — Helix users now have their own bucket.
  // CR-5367 rework: supplier admins land on the supplier tab so they
  // see their own colleagues by default; everyone else lands on
  // customer (the historical default).
  const supplierUserDefault =
    (user?.company_type === 'supplier' || user?.platform === 'SUPPLIER');
  const [userTypeTab, setUserTypeTab] = useState<'customer' | 'supplier' | 'valuechain'>(
    supplierUserDefault ? 'supplier' : 'customer'
  );

  // CR-5312: confirm-delete dialog state.
  const [deleteCandidate, setDeleteCandidate] = useState<UserProfile | null>(null);

  // Permissions
  /** Only Django superusers may grant/revoke target users' is_superuser/is_staff (modal switch). */
  const isSuperAdmin = Boolean(user?.is_superuser);
  // CR-5367 rework: Valuechain Users tab is internal Helix-only. Gate
  // strictly on the home company (platform/company_type). Previously
  // accepted is_superuser as a fallback, which leaked the tab to any
  // customer / supplier account that happened to have is_superuser set
  // on it via test data.
  const isHelixViewer = user?.platform === 'HELIX' || user?.company_type === 'helix';
  // Relinking a user to another company is a platform repair tool —
  // helix staff / superusers only (backend enforces the same gate).
  const canRelinkCompany = isHelixViewer || Boolean(user?.is_superuser) || Boolean(user?.is_staff);
  const isSupplierViewer = user?.company_type === 'supplier' || user?.platform === 'SUPPLIER';
  // CR-5367 rework: user.role is a string label (e.g. "Customer Admin")
  // — the prior cast to Record<string, boolean> always returned
  // undefined and silently denied customer/supplier admins this page.
  // The top-level is_company_admin / can_manage_users fields are the
  // authoritative source.
  const canManageUsers = isHelixViewer || user?.is_staff || user?.is_superuser ||
    Boolean(user?.is_company_admin) || Boolean(user?.can_manage_users);

  // CR-6273: who may use "Login As" (now reachable only from the user-detail
  // view). VC-internal admins, or a customer admin — never a supplier user.
  // Mirrors the backend ImpersonateView eligibility check exactly: Helix
  // company type, Django superuser, or a (non-supplier) customer admin.
  // PR #630 review: is_staff alone is NOT accepted by the backend, so it
  // must not gate the button here — otherwise a non-Helix staff account
  // would see a "Login As" button that always 403s on click.
  const canImpersonate =
    isHelixViewer || Boolean(user?.is_superuser) ||
    ((Boolean(user?.is_company_admin) || Boolean(user?.can_manage_users)) && !isSupplierViewer);

  // CR-6273: impersonate the inspected user. The list is already scoped to the
  // active company/site view, and Helix targets are blocked here and on the
  // server, so this stays within the admin's hierarchy. Passing the user's
  // company as view_company_id pins the impersonated session to that tenant.
  const handleLoginAs = async (userProfile: UserProfile) => {
    if (userProfile.company_type === 'helix') return;
    try {
      const response = await authAPI.impersonate(
        userProfile.user.id, undefined, userProfile.company,
      );
      const data = response.data.data;
      startImpersonation(
        data.target_user,
        data.target_token,
        data.original_token,
        undefined,
        data.audit_log_id,
      );
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string; error?: string } } };
      toast.error(
        axiosErr.response?.data?.message
        || axiosErr.response?.data?.error
        || 'Failed to start Login As session',
      );
    }
  };

  /** Re-fetch the list at the active view scope. */
  const refreshUsers = () => actions.fetchUsers({
    view_level: viewLevel,
    group: selectedGroupId && selectedGroupId !== 0 ? selectedGroupId : null,
    company: selectedCompanyId ?? null,
    site: selectedSiteId ?? null,
  });

  useEffect(() => {
    if (!canManageUsers) { navigate('/dashboard'); return; }
    // CR-5374: re-fetch when the view-context scope changes so the
    // Customer / Supplier tab cohorts narrow to the active Group /
    // Division / Site. selectedGroupId === 0 is the synthetic
    // "All Companies" rollup — treat as no scope so the backend
    // returns the platform-wide list.
    actions.fetchUsers({
      view_level: viewLevel,
      group: selectedGroupId && selectedGroupId !== 0 ? selectedGroupId : null,
      company: selectedCompanyId ?? null,
      site: selectedSiteId ?? null,
    });
  }, [canManageUsers, navigate, viewLevel, selectedGroupId, selectedCompanyId, selectedSiteId]);

  useEffect(() => { setCurrentPage(1); }, [searchTerm, statusFilter, approvalFilter, companyFilter, roleFilter]);

  // --- Computed Values ---

  const { users } = actions;
  // CR-5367: Customer tab now strictly excludes Helix users (which have
  // company_type='helix' and were previously falling into this bucket).
  const customerCount = useMemo(
    () => users.filter(u => u.company_type !== 'supplier' && u.company_type !== 'helix').length,
    [users]
  );
  const supplierCount = useMemo(() => users.filter(u => u.company_type === 'supplier').length, [users]);
  const valuechainCount = useMemo(() => users.filter(u => u.company_type === 'helix').length, [users]);
  const pendingInSelectionCount = useMemo(() => {
    return users.filter((u) => selectedUsers.has(u.id) && !u.is_approved).length;
  }, [users, selectedUsers]);
  const uniqueCompanies = useMemo(() => Array.from(new Set(users.map(u => u.company_name))).sort(), [users]);
  const uniqueRoles = useMemo(() => Array.from(new Set(users.map(u => u.role_name).filter(Boolean))).sort(), [users]);

  const filteredAndSortedUsers = useMemo(() => {
    // CR-5367: three-way split — supplier, valuechain (internal Helix), or
    // customer (everything else, e.g. customer + logistics).
    // CR-5374 defence-in-depth: even if an external user deep-links to
    // ?tab=valuechain (or stale state lingers), never reveal helix users
    // in the rendered list.
    let filtered = users.filter(u => {
      if (userTypeTab === 'valuechain' && !isHelixViewer) return false;
      if (userTypeTab === 'supplier') return u.company_type === 'supplier';
      if (userTypeTab === 'valuechain') return u.company_type === 'helix';
      return u.company_type !== 'supplier' && u.company_type !== 'helix';
    });
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(u =>
        u.user.full_name.toLowerCase().includes(search) ||
        u.user.email.toLowerCase().includes(search) ||
        u.company_name.toLowerCase().includes(search) ||
        u.job_title?.toLowerCase().includes(search) ||
        u.department?.toLowerCase().includes(search)
      );
    }
    if (statusFilter === 'invited') {
      filtered = filtered.filter(isAwaitingInvite);
    } else if (statusFilter === 'active') {
      // "Active" means the badge that reads Active — an invited-but-not-joined
      // row is still is_active, so filtering on that flag alone would include
      // rows the column labels Invite Sent.
      filtered = filtered.filter(u => u.user.is_active && !isAwaitingInvite(u));
    } else if (statusFilter === 'inactive') {
      filtered = filtered.filter(u => !u.user.is_active);
    }
    if (approvalFilter !== 'all') filtered = filtered.filter(u => u.is_approved === (approvalFilter === 'approved'));
    if (companyFilter !== 'all') filtered = filtered.filter(u => u.company_name === companyFilter);
    if (roleFilter !== 'all') filtered = filtered.filter(u => u.role_name === roleFilter);

    filtered.sort((a, b) => {
      if (sortField === 'last_login') {
        const aLogin = a.user.last_login ? new Date(a.user.last_login).getTime() : 0;
        const bLogin = b.user.last_login ? new Date(b.user.last_login).getTime() : 0;
        if (aLogin < bLogin) return sortDirection === 'asc' ? -1 : 1;
        if (aLogin > bLogin) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      }
      let aVal: string | Date;
      let bVal: string | Date;
      switch (sortField) {
        case 'name': aVal = a.user.full_name; bVal = b.user.full_name; break;
        case 'email': aVal = a.user.email; bVal = b.user.email; break;
        case 'company': aVal = a.company_name; bVal = b.company_name; break;
        case 'role': aVal = a.role_name; bVal = b.role_name; break;
        case 'created_at': aVal = new Date(a.created_at); bVal = new Date(b.created_at); break;
        default: return 0;
      }
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return filtered;
  }, [users, userTypeTab, searchTerm, statusFilter, approvalFilter, companyFilter, roleFilter, sortField, sortDirection]);

  const totalPages = Math.ceil(filteredAndSortedUsers.length / itemsPerPage);
  const paginatedUsers = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredAndSortedUsers.slice(start, start + itemsPerPage);
  }, [filteredAndSortedUsers, currentPage, itemsPerPage]);

  const hasActiveFilters = searchTerm || statusFilter !== 'all' || approvalFilter !== 'all' ||
    companyFilter !== 'all' || roleFilter !== 'all';

  // Stats
  const joinedCount = useMemo(() => filteredAndSortedUsers.filter(u => u.user.has_joined).length, [filteredAndSortedUsers]);
  const notJoinedCount = filteredAndSortedUsers.length - joinedCount;
  const invitedSentCount = useMemo(() =>
    filteredAndSortedUsers.filter(u => !u.user.has_joined && u.user.invitation_sent).length,
    [filteredAndSortedUsers]);
  const notInvitedCount = useMemo(() =>
    filteredAndSortedUsers.filter(u => !u.user.has_joined && !u.user.invitation_sent).length,
    [filteredAndSortedUsers]);
  const uniqueCompanyCount = useMemo(() => new Set(filteredAndSortedUsers.map(u => u.company_name)).size, [filteredAndSortedUsers]);
  const joinedPercent = filteredAndSortedUsers.length > 0
    ? Math.round((joinedCount / filteredAndSortedUsers.length) * 100) : 0;

  // --- Handlers ---

  const isSelf = (userProfile: { user: { id: number } }) => userProfile.user.id === user?.id;

  const handleSort = (field: SortField) => {
    if (sortField === field) setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDirection('asc'); }
  };

  const toggleUserSelection = (userId: number) => {
    const next = new Set(selectedUsers);
    if (next.has(userId)) next.delete(userId); else next.add(userId);
    setSelectedUsers(next);
  };

  const toggleSelectAll = () => {
    const pageIds = paginatedUsers.map((u) => u.id);
    const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedUsers.has(id));
    const next = new Set(selectedUsers);
    if (allPageSelected) {
      pageIds.forEach((id) => next.delete(id));
    } else {
      pageIds.forEach((id) => next.add(id));
    }
    setSelectedUsers(next);
  };

  const selectAllNotInvited = () => {
    const ids = filteredAndSortedUsers
      .filter(u => !u.user.has_joined && !u.user.invitation_sent).map(u => u.id);
    setSelectedUsers(new Set(ids));
  };

  const clearFilters = () => {
    setSearchTerm(''); setStatusFilter('all'); setApprovalFilter('all');
    setCompanyFilter('all'); setRoleFilter('all');
  };

  const exportToCSV = () => exportUsersToCSV(filteredAndSortedUsers);

  const handleBulkApproveWrapped = async () => {
    await actions.handleBulkApprove(Array.from(selectedUsers));
    setSelectedUsers(new Set());
  };

  const handleApproveUserWrapped = async (userId: number) => {
    await actions.handleApproveUser(userId);
  };

  const handleBulkSendInvitationsWrapped = async () => {
    await actions.handleBulkSendInvitations(Array.from(selectedUsers));
    setSelectedUsers(new Set());
  };

  if (!canManageUsers) return null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
          <UsersIcon className="h-8 w-8" />Users Management
        </h1>
        <div className="flex gap-2">
          <Button
            onClick={refreshUsers}
            variant="outline"
            size="icon"
            title="Refresh"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button onClick={exportToCSV} variant="outline" className="gap-2">
            <Download className="h-4 w-4" />Export CSV
          </Button>
        </div>
      </div>

      {actions.error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" /><AlertDescription>{actions.error}</AlertDescription>
        </Alert>
      )}

      {/* Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card><CardContent className="pt-6"><div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-lg"><UsersIcon className="h-5 w-5 text-blue-600" /></div>
          <div><p className="text-2xl font-bold">{filteredAndSortedUsers.length}</p>
          <p className="text-sm text-muted-foreground">Total Users from {uniqueCompanyCount} companies</p></div>
        </div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="flex items-center gap-3">
          <div className="p-2 bg-green-100 rounded-lg"><UserCheck className="h-5 w-5 text-green-600" /></div>
          <div><p className="text-2xl font-bold text-green-600">{joinedCount}</p>
          <p className="text-sm text-muted-foreground">Users Joined ({joinedPercent}% of total)</p></div>
        </div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="flex items-center gap-3">
          <div className="p-2 bg-amber-100 rounded-lg"><Mail className="h-5 w-5 text-amber-600" /></div>
          <div><p className="text-2xl font-bold text-amber-600">{notJoinedCount}</p>
          <p className="text-sm text-muted-foreground">Not Joined ({invitedSentCount} invitations sent)</p></div>
        </div></CardContent></Card>
      </div>

      {/* Action Bar */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => navigate(`/users/invite?type=${userTypeTab}`)} className="gap-2">
            <UserPlus className="h-4 w-4" />Add User
          </Button>
          {selectedUsers.size > 0 && (
            <>
              <Button
                variant="outline"
                onClick={() => actions.setShowBulkApproveDialog(true)}
                disabled={actions.actionLoading || pendingInSelectionCount === 0}
                className="gap-2"
                title={pendingInSelectionCount === 0 ? 'No pending users in selection' : undefined}
              >
                <CheckCircle className="h-4 w-4" />
                Approve pending ({pendingInSelectionCount})
              </Button>
              <Button
                variant="outline"
                onClick={() => actions.setShowBulkInviteDialog(true)}
                disabled={actions.actionLoading}
                className="gap-2"
              >
                <Mail className="h-4 w-4" />
                Send invitations ({selectedUsers.size})
              </Button>
              <Button variant="outline" onClick={() => setSelectedUsers(new Set())}>Clear Selection</Button>
            </>
          )}
          {notInvitedCount > 0 && (
            <Button variant="outline" onClick={selectAllNotInvited} className="gap-2">
              <Mail className="h-4 w-4" />Select All Not Invited ({notInvitedCount})
            </Button>
          )}
        </div>
        <div className="flex gap-2 w-full md:w-auto">
          <div className="relative flex-1 md:w-[260px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search users..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
          </div>
        </div>
      </div>

      {/* Additional Filters */}
      <div className="flex flex-wrap gap-2">
        <Select value={approvalFilter} onValueChange={(v) => setApprovalFilter(v as ApprovalFilter)}>
          <SelectTrigger className="w-[150px]"><SelectValue placeholder="Approval" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Approvals</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
          </SelectContent>
        </Select>
        <Select value={companyFilter} onValueChange={setCompanyFilter}>
          <SelectTrigger className="w-[200px]"><SelectValue placeholder="Company" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Companies</SelectItem>
            {uniqueCompanies.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-[180px]"><SelectValue placeholder="User Role" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All User Roles</SelectItem>
            {uniqueRoles.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
          <SelectTrigger className="w-[140px]"><SelectValue placeholder="All Members" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Members</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="invited">Invite Sent</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters}><X className="h-4 w-4 mr-1" />Clear Filters</Button>
        )}
        <span className="text-sm text-muted-foreground self-center ml-2">
          Showing {paginatedUsers.length} of {filteredAndSortedUsers.length} users
        </span>
      </div>

      {/* CR-5367: Customer / Supplier / Valuechain Tabs.
          CR-5374: Valuechain tab hidden from external (non-Helix) viewers.
          CR-5367 rework: supplier admins only manage their own company —
          the Customer / Valuechain tabs are noise (and a perceived
          cross-tenant leak), so suppliers see no tab strip and the
          list shows just their supplier-side colleagues. */}
      {!isSupplierViewer && (
        <Tabs value={userTypeTab} onValueChange={(v) => { setUserTypeTab(v as 'customer' | 'supplier' | 'valuechain'); setCurrentPage(1); }}>
          <TabsList>
            <TabsTrigger value="customer" className="gap-2">
              <Building2 className="h-4 w-4" />Customer Users<Badge variant="secondary" className="ml-1">{customerCount}</Badge>
            </TabsTrigger>
            <TabsTrigger value="supplier" className="gap-2">
              <Truck className="h-4 w-4" />Supplier Users<Badge variant="secondary" className="ml-1">{supplierCount}</Badge>
            </TabsTrigger>
            {isHelixViewer && (
              <TabsTrigger value="valuechain" className="gap-2">
                <Shield className="h-4 w-4" />Valuechain Users<Badge variant="secondary" className="ml-1">{valuechainCount}</Badge>
              </TabsTrigger>
            )}
          </TabsList>
        </Tabs>
      )}

      {/* Table */}
      <UserTable
        users={paginatedUsers} selectedUsers={selectedUsers}
        onToggleSelection={toggleUserSelection} onToggleSelectAll={toggleSelectAll}
        onViewDetails={actions.openUserDetails} onToggleActive={actions.handleToggleActive}
        onChangeRole={actions.openChangeRoleDialog} onSendInvitation={actions.handleSendInvitation}
        onChangeCompany={canRelinkCompany ? actions.openChangeCompanyDialog : undefined}
        onApprove={handleApproveUserWrapped}
        onSafeDelete={(u) => setDeleteCandidate(u)}
        onViewActivity={actions.handleViewActivityLog}
        canImpersonate={canImpersonate} onLoginAs={handleLoginAs}
        isSelf={isSelf} loading={actions.loading} actionLoading={actions.actionLoading}
        sortField={sortField} sortDirection={sortDirection} onSort={handleSort}
        currentPage={currentPage} totalPages={totalPages}
        totalFiltered={filteredAndSortedUsers.length} onPageChange={setCurrentPage}
      />

      {/* CR-5312: confirm before delete/deactivate. Backend decides
          which one based on the user's login + audit footprint, but
          surface the *intent* to the admin so they understand why
          inactive users can't be hard-deleted. */}
      <Dialog open={!!deleteCandidate} onOpenChange={(open) => { if (!open) setDeleteCandidate(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Trash2 className="h-5 w-5" />
              Delete user
            </DialogTitle>
            <DialogDescription>
              {deleteCandidate ? (
                <>
                  <span className="font-medium">{deleteCandidate.user.full_name || deleteCandidate.user.email}</span>
                  {' '}will be{' '}
                  <span className="font-medium">permanently deleted</span>.
                  If they have login or audit history, their records are kept
                  under an anonymised identity — either way the account is
                  removed and the email address can be invited again.
                </>
              ) : null}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteCandidate(null)} disabled={actions.actionLoading}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={actions.actionLoading}
              onClick={async () => {
                if (!deleteCandidate) return;
                const target = deleteCandidate;
                setDeleteCandidate(null);
                await actions.handleSafeDelete(target);
              }}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialogs */}
      <UserDetailsModal
        open={actions.showUserDetails} onOpenChange={actions.setShowUserDetails}
        selectedUser={actions.selectedUser} userDetailTab={actions.userDetailTab}
        setUserDetailTab={actions.setUserDetailTab}
        userSitePermissions={actions.userSitePermissions}
        loadingSitePermissions={actions.loadingSitePermissions}
        savingPermission={actions.savingPermission}
        savingDivision={actions.savingDivision}
        isSuperAdmin={isSuperAdmin || false} actionLoading={actions.actionLoading}
        isSelf={isSelf} handleToggleActive={actions.handleToggleActive}
        openChangeRoleDialog={actions.openChangeRoleDialog}
        openChangeCompanyDialog={canRelinkCompany ? actions.openChangeCompanyDialog : undefined}
        handleToggleAdmin={actions.handleToggleAdmin}
        handleToggleSiteLink={actions.handleToggleSiteLink}
        handleToggleSitePermission={actions.handleToggleSitePermission}
        handleAssignDivision={actions.handleAssignDivision}
        handleApproveUser={handleApproveUserWrapped}
        handleRejectUser={actions.handleRejectUser}
        handleSendInvitation={actions.handleSendInvitation}
        canImpersonate={canImpersonate}
        handleLoginAs={handleLoginAs}
        onPrimaryContactsSaved={refreshUsers}
      />
      <BulkApproveDialog
        open={actions.showBulkApproveDialog} onOpenChange={actions.setShowBulkApproveDialog}
        selectedCount={selectedUsers.size}
        pendingInSelectionCount={pendingInSelectionCount}
        actionLoading={actions.actionLoading}
        onBulkApprove={handleBulkApproveWrapped}
      />
      <BulkSendInvitationsDialog
        open={actions.showBulkInviteDialog}
        onOpenChange={actions.setShowBulkInviteDialog}
        selectedCount={selectedUsers.size}
        actionLoading={actions.actionLoading}
        onConfirm={handleBulkSendInvitationsWrapped}
      />
      <ChangeCompanyDialog
        open={actions.showChangeCompanyDialog} onOpenChange={actions.setShowChangeCompanyDialog}
        selectedUser={actions.selectedUser} actionLoading={actions.actionLoading}
        onRelink={actions.handleRelinkCompany}
      />
      <ChangeRoleDialog
        open={actions.showChangeRoleDialog} onOpenChange={actions.setShowChangeRoleDialog}
        selectedUser={actions.selectedUser} availableRoles={actions.availableRoles}
        selectedRoleId={actions.selectedRoleId} setSelectedRoleId={actions.setSelectedRoleId}
        actionLoading={actions.actionLoading} onChangeRole={actions.handleChangeRole}
      />
      <ActivityLogDialog
        open={actions.showActivityLogDialog} onOpenChange={actions.setShowActivityLogDialog}
        selectedUser={actions.selectedUser} activityLogs={actions.activityLogs}
      />
    </div>
  );
}
